import React, { useState, useEffect, useRef } from 'react';
import Game from '../components/main/Game';
import Header from '../components/main/Header';
import { getBasicData } from '../helper/userHelperFunctions';
import './styles/Main.css';
import EndModal, { getScoreClass } from '../components/modals/EndModal';
import { getAllBoards, getTodaysBoard, getBoardByBoardNumber, submitUserData } from '../services/api';
import { getActiveStreakBoards, getBestStreak, getGameDataProperty, getPlayStreak } from '../services/state';
import { getBoardDifficulty } from '../helper/helperFunctions';
import { useThemePreference } from '../helper/useThemePreference';
import { hasTutorialBeenCompleted, markTutorialCompleted } from '../helper/tutorialPreference';
import { tutorial_board } from '../constants';
import { error as logError } from '../helper/logger';
import PrivacyPolicy from './PrivacyPolicy';

const MAX_BOARD_HISTORY = 6;

const buildBoardOptions = (latestBoardNumber, includeAllBoards = false) => {
    if (!latestBoardNumber || latestBoardNumber < 1) return [];
    if (includeAllBoards) {
        return Array.from({ length: latestBoardNumber }, (_, index) => index + 1);
    }

    const count = Math.min(MAX_BOARD_HISTORY, latestBoardNumber);
    return Array.from({ length: count }, (_, index) => latestBoardNumber - index);
};

const getArchivedBoardNumber = () => {
    const match = window.location.pathname.match(/^\/archive\/(\d+)\/?$/);
    if (!match) return null;

    const boardNumber = Number(match[1]);
    return boardNumber > 0 ? boardNumber : null;
};

const isArchiveMenuPath = () => /^\/archive\/?$/.test(window.location.pathname);
const isPrivacyPolicyPath = () => /^\/privacypolicy\/?$/.test(window.location.pathname);

const Main = ({ analyticsConsent, onOpenCookiePreferences }) => {
    const [basicUserData, setBasicUserData] = useState({});
    const [currentBoardData, setCurrentBoardData] = useState({});
    const [latestBoardNum, setLatestBoardNum] = useState(null);
    const [showEndModal, setShowEndModal] = useState(false);
    const [gameDone, setGameDone] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showBoardMenu, setShowBoardMenu] = useState(isArchiveMenuPath());
    const [menuView, setMenuView] = useState(isArchiveMenuPath() ? 'archive' : 'main');
    const [theme, setTheme] = useThemePreference();
    const [pageView, setPageView] = useState(isPrivacyPolicyPath() ? 'privacy' : 'game');
    const [boardDifficultyByNum, setBoardDifficultyByNum] = useState({});
    const hasFetchedBoardDifficultiesRef = useRef(false);
    const [tutorialCompleted, setTutorialCompleted] = useState(() => hasTutorialBeenCompleted());

    useEffect(() => {
        const syncPageView = () => {
            setPageView(isPrivacyPolicyPath() ? 'privacy' : 'game');
        };

        syncPageView();
        window.addEventListener('popstate', syncPageView);
        return () => window.removeEventListener('popstate', syncPageView);
    }, []);

    useEffect(() => {
        const fetchBasicData = async () => {
            try {
                const response = await getBasicData();
                setBasicUserData(response);

                // // Submit the user data to the server
                submitUserData({ user_browser_id: response.userId });
            } catch (err) {
                logError('Error fetching basic data:', err);
            }
        };

        const fetchBoardData = async () => {
            try {
                const archivedBoardNum = getArchivedBoardNumber();
                const todaysBoard = await getTodaysBoard();
                const board = archivedBoardNum
                    ? await getBoardByBoardNumber(archivedBoardNum)
                    : todaysBoard;
                const boardNum = board['board_num'];
                setLatestBoardNum(todaysBoard['board_num']);
                setCurrentBoardData({
                    boardNum,
                    targetNums: board['target_nums'],
                    operators: board['operators'],
                    solution_length: board['solution_length'],
                    solution: board['solution']
                });
                const playedGame = getGameDataProperty(boardNum, 'gameScore') !== null;
                setGameDone(playedGame);
                setShowEndModal(playedGame);
            } catch (err) {
                logError('Error fetching board:', err);
                setError('Failed to load board data.');
            } finally {
                setLoading(false);
            }
        };

        fetchBasicData();
        fetchBoardData();
    }, []);

    // Difficulty badges need every board's operators/solution_length, which the
    // per-board endpoints don't carry for boards you haven't opened yet - fetch the
    // bulk board list lazily, once, the first time the Archive view is opened.
    useEffect(() => {
        if (menuView !== 'archive' || hasFetchedBoardDifficultiesRef.current) return;
        hasFetchedBoardDifficultiesRef.current = true;

        (async () => {
            try {
                const boards = await getAllBoards();
                const difficultyByNum = {};
                boards.forEach((board) => {
                    difficultyByNum[board['board_num']] = getBoardDifficulty({
                        operators: board['operators'],
                        solution_length: board['solution_length']
                    });
                });
                setBoardDifficultyByNum(difficultyByNum);
            } catch (err) {
                logError('Error fetching all boards for difficulty badges:', err);
                hasFetchedBoardDifficultiesRef.current = false;
            }
        })();
    }, [menuView]);

    const loadBoard = async (boardNum) => {
        try {
            setLoading(true);
            const board = await getBoardByBoardNumber(boardNum);
            const hasPlayed = getGameDataProperty(boardNum, 'gameScore') !== null;
            setCurrentBoardData({
                boardNum: board['board_num'],
                targetNums: board['target_nums'],
                operators: board['operators'],
                solution_length: board['solution_length'],
                solution: board['solution']
            });
            setGameDone(hasPlayed);
            setShowEndModal(hasPlayed);
        } catch (err) {
            logError('Error loading board', boardNum, err);
            setError(`Could not load board ${boardNum}.`);
        } finally {
            setLoading(false);
        }
    };

    const handleMenuToggle = () => {
        setShowBoardMenu((visible) => {
            if (visible) setMenuView('main');
            return !visible;
        });
    };

    const handleBoardSelect = async (boardNum) => {
        setShowBoardMenu(false);
        setMenuView('main');
        const targetPath = Number(boardNum) === Number(latestBoardNum) ? '/' : `/archive/${boardNum}`;
        window.history.pushState({}, '', targetPath);
        if (boardNum !== currentBoardData.boardNum) {
            await loadBoard(boardNum);
        }
    };

    const handleCookiePreferencesOpen = () => {
        setShowBoardMenu(false);
        setMenuView('main');
        onOpenCookiePreferences?.();
    };

    const handlePrivacyPolicyOpen = () => {
        setShowBoardMenu(false);
        setMenuView('main');
        window.history.pushState({}, '', '/privacypolicy');
        setPageView('privacy');
    };

    const handlePrivacyPolicyClose = () => {
        window.history.pushState({}, '', '/');
        setShowBoardMenu(false);
        setMenuView('main');
        setPageView('game');
    };

    const handleTutorialComplete = () => {
        markTutorialCompleted();
        setTutorialCompleted(true);
        // The tutorial's own completion set these on the shared gameDone/showEndModal
        // state - clear them so today's real board starts fresh, not already "done".
        setGameDone(false);
        setShowEndModal(false);
    };

    if (error) {
        return <div>Error: {error}</div>;
    }

    // New players (no completed boards yet) get a hand-designed, easier tutorial
    // board first - only on the plain "/" path, never for a directly-linked archived
    // board, so a shared board link never gets hijacked into the tutorial.
    const showTutorial = !tutorialCompleted && !getArchivedBoardNumber();
    const boardDataForGame = showTutorial ? tutorial_board : currentBoardData;

    // Check if currentBoardData has the necessary properties (the tutorial board is
    // a hardcoded constant, always valid, and boardNum 0 would fail this truthy check)
    const availableBoards = buildBoardOptions(latestBoardNum, true);
    const isBoardDataValid = showTutorial || (currentBoardData.boardNum && currentBoardData.targetNums && currentBoardData.operators && currentBoardData.solution_length);

    if (pageView === 'privacy') {
        return (
            <div className="main-container">
                <PrivacyPolicy onBack={handlePrivacyPolicyClose} />
            </div>
        );
    }

    return (
        <div className="main-container">
            <Header onMenuToggle={handleMenuToggle} currentBoardNum={currentBoardData.boardNum} subtitle={showTutorial ? 'Tutorial' : undefined} />
            {showBoardMenu && (
                <div className="board-menu-overlay" role="dialog" aria-modal="true">
                    <div className="board-menu-panel">
                        <div className="board-menu-header">
                            <h2>{menuView === 'main' ? 'Menu' : menuView === 'archive' ? 'Archive' : menuView === 'info' ? 'Info' : 'Settings'}</h2>
                            <button className="board-menu-close" onClick={() => { setShowBoardMenu(false); setMenuView('main'); }} aria-label="Close menu">×</button>
                        </div>
                        <div className="board-menu-body">
                            {menuView === 'main' && (() => {
                                const currentStreak = getPlayStreak(latestBoardNum);
                                const todayDone = getGameDataProperty(latestBoardNum, 'gameScore') !== null;
                                return (
                                    <>
                                        {currentStreak > 0 && !todayDone && (
                                            <p className="board-menu-streak-nudge">🔥 {currentStreak}-day streak — play today's board to keep it going!</p>
                                        )}
                                        <div className="board-menu-grid">
                                            <button className="board-menu-button" onClick={() => setMenuView('info')}>Info</button>
                                            <button className="board-menu-button" onClick={() => setMenuView('archive')}>Archive</button>
                                            <button className="board-menu-button" onClick={() => setMenuView('settings')}>Settings</button>
                                            <button className="board-menu-button" onClick={handlePrivacyPolicyOpen}>Privacy Policy</button>
                                            <button className="board-menu-button feedback-link" onClick={() => window.open('https://forms.gle/sPkA6rabBUqNnnmE8', '_blank', 'noopener,noreferrer')}>Feedback ↗</button>
                                        </div>
                                    </>
                                );
                            })()}
                            {menuView === 'archive' && (() => {
                                const streakBoards = new Set(getActiveStreakBoards(latestBoardNum));
                                const bestStreak = getBestStreak();
                                return (
                                    <>
                                        <button className="board-menu-back" onClick={() => setMenuView('main')}>Back to menu</button>
                                        {streakBoards.size > 0 && (
                                            <p className="board-menu-streak">
                                                🔥 {streakBoards.size} day{streakBoards.size === 1 ? '' : 's'} streak
                                                {bestStreak > streakBoards.size ? ` (best: ${bestStreak})` : ' — new best!'}
                                            </p>
                                        )}
                                        <div className="board-menu-grid">
                                            {availableBoards.map((boardNum) => {
                                                const isCompleted = getGameDataProperty(boardNum, 'gameScore') !== null;
                                                const gameScore = getGameDataProperty(boardNum, 'gameScore');
                                                const inStreak = streakBoards.has(boardNum);
                                                return (
                                                    <button
                                                        key={boardNum}
                                                        className={`board-menu-button ${boardNum === currentBoardData.boardNum ? 'selected' : ''} ${isCompleted ? 'completed' : ''}`}
                                                        onClick={() => handleBoardSelect(boardNum)}
                                                        aria-label={`Board ${boardNum}${isCompleted ? ', completed' : ''}${inStreak ? ', part of current streak' : ''}`}
                                                    >
                                                        {isCompleted && <span className="board-menu-check" aria-hidden="true">{inStreak ? '🔥' : '✓'}</span>}
                                                        <span className="board-menu-number">Board #{boardNum}</span>
                                                        {boardDifficultyByNum[boardNum] && (
                                                            <span className={`board-difficulty-badge board-menu-difficulty board-difficulty-${boardDifficultyByNum[boardNum].toLowerCase()}`}>
                                                                {boardDifficultyByNum[boardNum]}
                                                            </span>
                                                        )}
                                                        {isCompleted && <span className={`board-menu-score ${getScoreClass(gameScore)}`}>{gameScore}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                );
                            })()}
                            {menuView === 'info' && (
                                <div className="board-menu-content">
                                    <p>The Combos is a daily arithmetic puzzle: turn the starting number into each target number, in order, using the given operators.</p>
                                    <p>The move counter (e.g. 4 / 11) shows moves used out of the move limit. The limit is fixed: you must solve the board in exactly that many moves, no more and no fewer.</p>
                                    <p>Score (0–100) is weighted 80% on completion time and 20% on steps taken beyond the minimum.</p>
                                    <p>A new board releases daily at midnight UTC time.</p>
                                    <p>Send feedback or bug reports via the Feedback link in the menu.</p>
                                    <button className="board-menu-back" onClick={() => setMenuView('main')}>Back to menu</button>
                                </div>
                            )}
                            {menuView === 'settings' && (
                                <div className="board-menu-content">
                                    <p>Theme: {theme === 'light' ? 'Light' : 'Dark'}</p>
                                    <button className="board-menu-button" onClick={() => setTheme((current) => (current === 'light' ? 'dark' : 'light'))}>
                                        Switch to {theme === 'light' ? 'Dark' : 'Light'} mode
                                    </button>
                                    <p>Analytics: {analyticsConsent ? 'Accepted' : 'Opted out'}</p>
                                    <button className="board-menu-button" onClick={handleCookiePreferencesOpen}>Manage cookie preferences</button>
                                    <button className="board-menu-back" onClick={() => setMenuView('main')}>Back to menu</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <main className="main-content">
                {(loading && !showTutorial) ? (
                    <div className="main-loading">Loading...</div>
                ) : isBoardDataValid ? (
                    <Game
                        key={showTutorial ? 'tutorial' : boardDataForGame.boardNum}
                        basicUserData={basicUserData}
                        currentBoardData={boardDataForGame}
                        latestBoardNum={latestBoardNum}
                        onNavigateToBoard={handleBoardSelect}
                        isArchived={Boolean(getArchivedBoardNumber())}
                        archiveMenuOpen={isArchiveMenuPath() && showBoardMenu}
                        skipHowToPlay={isArchiveMenuPath() && showBoardMenu}
                        gameDone={gameDone}
                        setGameDone={setGameDone}
                        setShowEndModal={setShowEndModal}
                        showEndModal={showEndModal}
                        isTutorial={showTutorial}
                    />
                ) : (
                    <div>No board data available.</div>
                )}
            </main>

            {showEndModal && (
                <EndModal
                    setShowEndModal={showTutorial ? handleTutorialComplete : setShowEndModal}
                    gameDone={gameDone}
                    boardNum={boardDataForGame.boardNum}
                    latestBoardNum={latestBoardNum}
                    onNavigateToBoard={handleBoardSelect}
                    isArchived={Boolean(getArchivedBoardNumber())}
                    isTutorial={showTutorial}
                />
            )}
        </div>
    );
};

export default Main;